import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api";
import "../assets/datatables.css";
import "../assets/vacantRooms.css";

const VacantRooms = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [pending, setPending] = useState(true);
  const [error, setError] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(30);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    propertyName: true,
    propertyId: true,
    vacantRoomsNumber: true,
    vacantRoomsNames: true,
    postcode: true,
    actions: true,
  });

  // Column visibility dropdown state
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);

  useEffect(() => {
    console.log("🔍 [FRONTEND] VacantRooms component mounted");
    console.log("🔍 [FRONTEND] Making API call to /properties/vacant-rooms");
    console.log("🔍 [FRONTEND] Current window location:", window.location.href);
    console.log("🔍 [FRONTEND] API base URL:", api.defaults.baseURL);

    const fullUrl = `${api.defaults.baseURL}/properties/vacant-rooms`;
    console.log("🔍 [FRONTEND] Full URL being called:", fullUrl);

    api
      .get("/properties/vacant-rooms")
      .then((res) => {
        console.log("✅ [FRONTEND] API response received:", res);
        console.log("📊 [FRONTEND] Response data:", res.data);
        console.log("📊 [FRONTEND] Response status:", res.status);
        console.log("📊 [FRONTEND] Data source:", res.data.source);

        // Handle new response structure with data wrapper
        if (res.data.data && Array.isArray(res.data.data)) {
          console.log(
            "✅ [FRONTEND] Data is wrapped in data property, extracting properties"
          );
          console.log(`📊 [FRONTEND] Data source: ${res.data.source}`);
          console.log(`📊 [FRONTEND] Total properties: ${res.data.total}`);
          setProperties(res.data.data);
        } else if (Array.isArray(res.data)) {
          // Fallback for legacy response format
          console.log("✅ [FRONTEND] Data is direct array (legacy format)");
          setProperties(res.data);
        } else {
          console.log(
            "❌ [FRONTEND] Unexpected response format:",
            typeof res.data
          );
          setError(res.data.message || "Unexpected response from server.");
        }
        setPending(false);
      })
      .catch((err) => {
        console.log("❌ [FRONTEND] API call failed:", err);
        console.log("❌ [FRONTEND] Error response:", err.response);
        console.log("❌ [FRONTEND] Error message:", err.message);
        console.log("❌ [FRONTEND] Error config:", err.config);
        setError("Failed to load vacant rooms.");
        setPending(false);
      });
  }, []);

  // Action button handlers - Matching Laravel functionality
  const handleShow = (propertyId) => {
    navigate(`/properties/${propertyId}`);
  };

  const handleEdit = (propertyId) => {
    navigate(`/properties/${propertyId}/edit`);
  };

  const handleRemove = (propertyId, propertyName) => {
    if (
      window.confirm(
        `Are you sure you want to remove ${propertyName}?\n\nThis will permanently delete:\n• The property and all its data\n• All related rooms, rents, and viewings\n• All photos, documents, and reports\n• All tickets and inspection records\n\nThis action cannot be undone.`
      )
    ) {
      // Implement delete API call to /properties/{id}/destroy
      console.log(
        `🗑️ [FRONTEND] Deleting property ${propertyId} and all related records`
      );

      api
        .delete(`/properties/${propertyId}/destroy`)
        .then((response) => {
          console.log(
            "✅ [FRONTEND] Property deletion successful:",
            response.data
          );

          // Show success message with details
          const deletedRecords = response.data.data?.deleted_records || [];
          const message = `Property "${propertyName}" successfully deleted!\n\nDeleted records:\n${deletedRecords
            .map((record) => `• ${record}`)
            .join("\n")}`;

          alert(message);

          // Refresh the list
          window.location.reload();
        })
        .catch((err) => {
          console.error("❌ [FRONTEND] Error deleting property:", err);
          const errorMessage =
            err.response?.data?.error || "Failed to delete property";
          alert(`Error: ${errorMessage}`);
        });
    }
  };

  const handlePayments = (propertyId) => {
    navigate(`/properties/${propertyId}/payments`);
  };

  const handlePhotos = (propertyId) => {
    navigate(`/properties/${propertyId}/photos`);
  };

  const handleViewings = (propertyId) => {
    navigate(`/properties/${propertyId}/viewings`);
  };

  const handleMonthlyInspections = (propertyId) => {
    navigate(`/properties/${propertyId}/monthly-inspections`);
  };

  const handleCopyAddress = (property) => {
    // Create full address matching Laravel's getFullAddressForCopyAttribute() method
    let address = property.address_1 || "";

    if (property.address_2) {
      address = address + " " + property.address_2;
    }
    if (property.address_3) {
      address = address + " " + property.address_3;
    }

    address =
      address.trim() +
      ", " +
      (property.town || "").trim() +
      ", " +
      (property.postcode || "").trim();

    // Use Laravel's exact copy method (document.execCommand approach)
    const aux = document.createElement("input");
    aux.setAttribute("value", address);
    document.body.appendChild(aux);
    aux.select();
    document.execCommand("copy");
    document.body.removeChild(aux);

    // Show success feedback (matching Laravel behavior)
    console.log("Address copied to clipboard:", address);

    // Create a temporary success message (matching Laravel's feedback)
    const successMsg = document.createElement("div");
    successMsg.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #5cb85c;
      color: white;
      padding: 10px 15px;
      border-radius: 4px;
      z-index: 9999;
      font-size: 14px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    `;
    successMsg.textContent = "Address copied to clipboard!";
    document.body.appendChild(successMsg);

    // Remove the message after 3 seconds
    setTimeout(() => {
      if (successMsg.parentNode) {
        successMsg.parentNode.removeChild(successMsg);
      }
    }, 3000);
  };

  // CSV escaping helper function (matches Laravel DataTables CSV export)
  const escapeCSV = (value) => {
    if (value === null || value === undefined) {
      return "";
    }
    
    // Convert to string
    const stringValue = String(value);
    
    // Check if value needs to be quoted (contains comma, quote, or newline)
    if (
      stringValue.includes(",") ||
      stringValue.includes('"') ||
      stringValue.includes("\n")
    ) {
      // Escape quotes by doubling them and wrap in quotes
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    
    return stringValue;
  };

  // Export handlers - Laravel: Export as CSV (columns 0, 1, 2, 3, 4)
  const handleExportCSV = () => {
    // Use all properties (not just filtered/paginated) to match Laravel DataTables export
    const allProperties = properties;
    
    // Laravel columns: [0, 1, 2, 3, 4] = Property Name, Property ID, Vacant rooms number, Vacant rooms names, Postcode
    const csvRows = [];
    
    // Header row (properly escaped)
    csvRows.push([
      escapeCSV("Property Name"),
      escapeCSV("Property ID"),
      escapeCSV("Vacant rooms number"),
      escapeCSV("Vacant rooms names"),
      escapeCSV("Postcode"),
    ].join(","));
    
    // Data rows (properly escaped)
    allProperties.forEach((property) => {
      const vacantRoomsNames = Array.isArray(property.vacantRoomsNames)
        ? property.vacantRoomsNames.join(", ")
        : (property.vacantRoomsNames || "");
      
      csvRows.push([
        escapeCSV(property.propertyName || ""),
        escapeCSV(property.propertyId || ""),
        escapeCSV(property.vacantRoomsNumber ?? ""),
        escapeCSV(vacantRoomsNames),
        escapeCSV(property.postcode || ""),
      ].join(","));
    });
    
    const csvContent = csvRows.join("\n");
    
    // Add BOM for UTF-8 to ensure proper encoding in Excel
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vacant_rooms_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    console.log(`✅ [VACANT ROOMS] Exported ${allProperties.length} properties to CSV`);
  };

  const handleExportPDF = async () => {
    try {
      console.log("📄 [VACANT ROOMS] Starting PDF export...");
      console.log("📊 [DEBUG] Properties count:", properties.length);
      
      // Check if we have data to export
      if (!properties || properties.length === 0) {
        alert("No data available to export. Please check your filters or try refreshing the page.");
        return;
      }
      
      // Dynamic import for jsPDF and autoTable with proper error handling
      // Import both in parallel (matching exportUtils.js pattern)
      console.log("📦 [DEBUG] Importing jsPDF and jspdf-autotable...");
      const [jsPDFModule, autoTableModule] = await Promise.all([
        import("jspdf"),
        import("jspdf-autotable")
      ]);
      
      const jsPDF = jsPDFModule.default || jsPDFModule;
      // jspdf-autotable v5.x exports autoTable as default or extends prototype
      // Try to get it from module, or it will be available on doc.autoTable
      let autoTable = null;
      if (autoTableModule.default && typeof autoTableModule.default === "function") {
        autoTable = autoTableModule.default;
      } else if (autoTableModule.autoTable && typeof autoTableModule.autoTable === "function") {
        autoTable = autoTableModule.autoTable;
      }
      
      console.log("📦 [DEBUG] jsPDF imported successfully");
      console.log("📦 [DEBUG] autoTable from module:", typeof autoTable);
      
      if (!jsPDF) {
        throw new Error("jsPDF library not loaded properly");
      }
      
      // Create PDF document in landscape orientation (better for tables with multiple columns)
      let doc;
      try {
        // Try newer API first
        doc = new jsPDF({
          orientation: "landscape",
          unit: "mm",
          format: "a4",
        });
      } catch (e) {
        console.log("New API failed, trying legacy API:", e);
        // Fallback to legacy API
        doc = new jsPDF("landscape", "mm", "a4");
      }
      
      console.log("📄 [DEBUG] jsPDF document created");
      
      // Get actual page dimensions from document
      const pageWidth = doc.internal.pageSize.getWidth();
      const availableWidth = pageWidth - 40; // 20mm margins on each side
      
      // Add title - centered and larger
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      const titleText = "Active Properties with Vacant Rooms Report";
      const titleWidth = doc.getTextWidth(titleText);
      doc.text(titleText, (pageWidth - titleWidth) / 2, 20);
      
      // Add metadata - better spacing
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      const dateText = `Generated on: ${new Date().toLocaleDateString("en-GB")}`;
      const totalText = `Total Properties: ${properties.length}`;
      doc.text(dateText, 20, 30);
      doc.text(totalText, 20, 37);
      
      // Debug: Log first property to check data structure
      if (properties.length > 0) {
        console.log("📊 [PDF] Sample property data:", properties[0]);
        console.log("📊 [PDF] All property keys:", Object.keys(properties[0]));
        console.log("📊 [PDF] Postcode value:", properties[0].postcode);
        console.log("📊 [PDF] Property name:", properties[0].propertyName);
        console.log("📊 [PDF] Property ID:", properties[0].propertyId);
      }
      
      // Prepare table data with all columns: Property Name | Property ID | Vacant Rooms | Vacant Rooms Names | Postcode
      const tableData = properties.map((property) => {
        // Ensure postcode is properly extracted (handle null/undefined)
        const postcode = property.postcode || property.post_code || property.postCode || "-";
        
        // Format vacant room names - handle both array and string formats
        let vacantRoomsNames = "-";
        if (property.vacantRoomsNames) {
          if (Array.isArray(property.vacantRoomsNames)) {
            vacantRoomsNames = property.vacantRoomsNames.length > 0 
              ? property.vacantRoomsNames.join(", ") 
              : "-";
          } else if (typeof property.vacantRoomsNames === "string") {
            vacantRoomsNames = property.vacantRoomsNames || "-";
          }
        } else if (property.vacant_rooms_names) {
          // Handle snake_case format
          if (Array.isArray(property.vacant_rooms_names)) {
            vacantRoomsNames = property.vacant_rooms_names.length > 0 
              ? property.vacant_rooms_names.join(", ") 
              : "-";
          } else if (typeof property.vacant_rooms_names === "string") {
            vacantRoomsNames = property.vacant_rooms_names || "-";
          }
        }
        
        return [
          String(property.propertyName || property.name || "-"),
          String(property.propertyId || property.property_id || "-"),
          String(property.vacantRoomsNumber ?? property.vacant_rooms_count ?? "0"),
          String(vacantRoomsNames),
          String(postcode),
        ];
      });
      
      console.log("📊 [PDF] Table data sample (first 3 rows):", tableData.slice(0, 3));
      console.log("📊 [PDF] autoTable function available:", typeof autoTable === "function");
      console.log("📊 [PDF] doc.autoTable available:", typeof doc.autoTable === "function");
      
      // Generate table using autoTable - try both function call and method call
      let autoTableSuccess = false;
      
      // Try function call syntax first: autoTable(doc, options) - matching exportUtils.js
      if (typeof autoTable === "function") {
        try {
          console.log("📊 [PDF] Using autoTable function call syntax");
          autoTable(doc, {
            startY: 45,
            head: [
              [
                "Property Name",
                "Property ID",
                "Vacant Rooms",
                "Vacant Rooms Names",
                "Postcode",
              ],
            ],
            body: tableData,
            theme: "striped",
            headStyles: {
              fillColor: [27, 32, 70], // Dark navy blue matching Laravel
              textColor: 255,
              fontStyle: "bold",
              fontSize: 10, // Slightly smaller header font
              halign: "left",
            },
            bodyStyles: {
              fontSize: 9, // Slightly smaller body font
              halign: "left",
            },
            columnStyles: {
              0: { cellWidth: 80, halign: "left" }, // Property Name
              1: { cellWidth: 40, halign: "left" }, // Property ID
              2: { cellWidth: 30, halign: "center" }, // Vacant Rooms (centered)
              3: { cellWidth: 70, halign: "left" }, // Vacant Rooms Names
              4: { cellWidth: 37, halign: "left" }, // Postcode
            },
            styles: {
              overflow: "linebreak",
              cellPadding: 3,
              lineWidth: 0.5,
              lineColor: [0, 0, 0],
              fontSize: 9, // Slightly smaller font for better fit
            },
            margin: { top: 45, left: 20, right: 20 },
            alternateRowStyles: {
              fillColor: [245, 245, 245],
            },
          });
          console.log("✅ [PDF] autoTable rendered successfully");
          autoTableSuccess = true;
        } catch (autoTableError) {
          console.error("❌ [PDF] autoTable error:", autoTableError);
          console.error("❌ [PDF] Error details:", autoTableError.message, autoTableError.stack);
          autoTableSuccess = false;
        }
      } else if (typeof doc.autoTable === "function") {
        // Fallback: Try method call syntax
        try {
          console.log("📊 [PDF] Using doc.autoTable method call");
          doc.autoTable({
            startY: 45,
            head: [
              [
                "Property Name",
                "Property ID",
                "Vacant Rooms",
                "Vacant Rooms Names",
                "Postcode",
              ],
            ],
            body: tableData,
            theme: "striped",
            headStyles: {
              fillColor: [27, 32, 70],
              textColor: 255,
              fontStyle: "bold",
              fontSize: 11,
              halign: "left",
            },
            bodyStyles: {
              fontSize: 10,
              halign: "left",
            },
            columnStyles: {
              0: { cellWidth: 80, halign: "left" },
              1: { cellWidth: 40, halign: "left" },
              2: { cellWidth: 30, halign: "center" },
              3: { cellWidth: 70, halign: "left" },
              4: { cellWidth: 37, halign: "left" },
            },
            styles: {
              overflow: "linebreak",
              cellPadding: 3,
              lineWidth: 0.5,
              lineColor: [0, 0, 0],
              fontSize: 9,
            },
            margin: { top: 45, left: 20, right: 20 },
            alternateRowStyles: {
              fillColor: [245, 245, 245],
            },
          });
          console.log("✅ [PDF] autoTable rendered successfully using method call");
          autoTableSuccess = true;
        } catch (autoTableError) {
          console.error("❌ [PDF] doc.autoTable method call error:", autoTableError);
          autoTableSuccess = false;
        }
      }
      
      // Fallback: Simple text rendering if autoTable is not available or failed
      if (!autoTableSuccess) {
        console.warn("⚠️ [PDF] autoTable not available, using fallback rendering");
        let yPos = 50;
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("Property Name | Property ID | Vacant Rooms | Vacant Rooms Names | Postcode", 20, yPos);
        yPos += 10;
        doc.setFont("helvetica", "normal");
        
        tableData.forEach((row) => {
          // Columns: 0=Name, 1=ID, 2=Rooms, 3=Room Names, 4=Postcode
          const text = `${row[0] || "-"} | ${row[1] || "-"} | ${row[2] || "0"} | ${row[3] || "-"} | ${row[4] || "-"}`;
          doc.text(text, 20, yPos);
          yPos += 8;
          if (yPos > 280) {
            doc.addPage();
            yPos = 20;
          }
        });
      }
      
      // Save PDF
      const filename = `vacant_rooms_${new Date().toISOString().split("T")[0]}.pdf`;
      doc.save(filename);
      
      console.log(`✅ [VACANT ROOMS] PDF exported successfully: ${filename}`);
      toast.success("PDF exported successfully!");
    } catch (error) {
      console.error("❌ [VACANT ROOMS] Error exporting PDF:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        name: error.name,
      });
      toast.error(`Failed to export PDF: ${error.message || "Unknown error"}`);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleColumnVisibility = () => {
    setShowColumnDropdown(!showColumnDropdown);
  };

  const toggleColumn = (columnName) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [columnName]: !prev[columnName],
    }));
  };

  // Handle checkbox clicks to prevent dropdown from closing
  const handleCheckboxClick = (e) => {
    e.stopPropagation();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only close if clicking outside the dropdown and not on the card content
      const dropdown = event.target.closest(".column-visibility-dropdown");
      const card = event.target.closest(".panel");
      const table = event.target.closest(".table");
      const controls = event.target.closest(".datatable-controls-bar");
      
      if (
        showColumnDropdown &&
        !dropdown &&
        !card &&
        !table &&
        !controls
      ) {
        setShowColumnDropdown(false);
      }
    };

    if (showColumnDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showColumnDropdown]);

  // DataTable filtering and pagination logic
  const filteredProperties = properties.filter((property) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      property.propertyName?.toLowerCase().includes(searchLower) ||
      property.propertyId?.toLowerCase().includes(searchLower) ||
      property.postcode?.toLowerCase().includes(searchLower) ||
      property.vacantRoomsNames?.some((name) =>
        name.toLowerCase().includes(searchLower)
      )
    );
  });

  const totalPages = Math.ceil(filteredProperties.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const paginatedProperties = filteredProperties.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleEntriesChange = (entries) => {
    setEntriesPerPage(entries);
    setCurrentPage(1); // Reset to first page when changing entries per page
  };

  return (
    <div className="container-fluid">
      {/* Breadcrumbs */}

      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{
            fontWeight: 400,
            fontSize: "14px",
            color: "black",
            cursor: "pointer",
          }}
          onClick={() => navigate("/dashboard")}
        >
          Dashboard
        </span>
        <span
          className="breadcrumb-sep"
          style={{ margin: "0 5px", color: "#666" }}
        >
          /
        </span>
        <span
          className="breadcrumb-main"
          style={{
            fontWeight: 400,
            fontSize: "14px",
            color: "black",
            cursor: "pointer",
          }}
          onClick={() => navigate("/properties")}
        >
          Properties
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Vacant Rooms</span>
        </div>
      </div>

      <div className="row">
        <div className="col-md-12">
          <div className="panel panel-default panel-standard">
            <div
              className="panel-heading panel-title"
              id="property-status-span"
            >
              <h3>Active properties with vacant rooms</h3>
            </div>
            <div className="panel-body">
              {error ? (
                <div className="alert alert-danger">{error}</div>
              ) : pending ? (
                <div className="text-center">
                  <p>Processing...</p>
                </div>
              ) : properties.length === 0 ? (
                <div className="text-center">
                  <p>No properties!</p>
                </div>
              ) : (
                <div className="dataTable_wrapper">
                  {/* DataTable Controls - Matching Laravel UI */}
                  <div className="datatable-controls-bar">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <div className="d-flex align-items-center me-4">
                          <label className="me-2">Show</label>
                          <select
                            className="form-control form-control-sm me-2"
                            style={{ width: "auto" }}
                            value={entriesPerPage}
                            onChange={(e) =>
                              handleEntriesChange(parseInt(e.target.value))
                            }
                          >
                            <option value={30}>30</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={75}>75</option>
                            <option value={100}>100</option>
                          </select>
                          <span>entries</span>
                        </div>
                        <div className="d-flex align-items-center">
                          <label className="me-2">Search:</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            style={{ width: "300px" }}
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="btn-group" role="group">
                        <button
                          type="button"
                          className="btn btn-default btn-sm"
                          onClick={() => handleExportCSV()}
                          title="Export as CSV"
                        >
                          Export as CSV
                        </button>
                        <button
                          type="button"
                          className="btn btn-default btn-sm"
                          onClick={() => handleExportPDF()}
                          title="Export as PDF"
                        >
                          Export as PDF
                        </button>
                        <div
                          style={{ position: "relative", display: "inline-block" }}
                        >
                          <button
                            type="button"
                            className="btn btn-default btn-sm"
                            onClick={handleColumnVisibility}
                            title="Toggle column visibility"
                          >
                            Column visibility
                          </button>

                          {/* Column Visibility Dropdown */}
                          {showColumnDropdown && (
                            <div
                              className="column-visibility-menu"
                              style={{
                                position: "absolute",
                                top: "100%",
                                right: 0,
                                background: "#fff",
                                border: "1px solid #ced4da",
                                borderRadius: "4px",
                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                                padding: "15px",
                                minWidth: "200px",
                                zIndex: 1000,
                              }}
                            >
                              <div>
                                <label
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    marginBottom: "8px",
                                    fontSize: "13px",
                                    cursor: "pointer",
                                  }}
                                  onClick={handleCheckboxClick}
                                >
                                  <input
                                    type="checkbox"
                                    checked={visibleColumns.propertyName}
                                    onChange={() => toggleColumn("propertyName")}
                                    onClick={handleCheckboxClick}
                                  />
                                  Property Name
                                </label>
                                <label
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    marginBottom: "8px",
                                    fontSize: "13px",
                                    cursor: "pointer",
                                  }}
                                  onClick={handleCheckboxClick}
                                >
                                  <input
                                    type="checkbox"
                                    checked={visibleColumns.propertyId}
                                    onChange={() => toggleColumn("propertyId")}
                                    onClick={handleCheckboxClick}
                                  />
                                  Property ID
                                </label>
                                <label
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    marginBottom: "8px",
                                    fontSize: "13px",
                                    cursor: "pointer",
                                  }}
                                  onClick={handleCheckboxClick}
                                >
                                  <input
                                    type="checkbox"
                                    checked={visibleColumns.vacantRoomsNumber}
                                    onChange={() =>
                                      toggleColumn("vacantRoomsNumber")
                                    }
                                    onClick={handleCheckboxClick}
                                  />
                                  Vacant rooms number
                                </label>
                                <label
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    marginBottom: "8px",
                                    fontSize: "13px",
                                    cursor: "pointer",
                                  }}
                                  onClick={handleCheckboxClick}
                                >
                                  <input
                                    type="checkbox"
                                    checked={visibleColumns.vacantRoomsNames}
                                    onChange={() =>
                                      toggleColumn("vacantRoomsNames")
                                    }
                                    onClick={handleCheckboxClick}
                                  />
                                  Vacant rooms names
                                </label>
                                <label
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    marginBottom: "8px",
                                    fontSize: "13px",
                                    cursor: "pointer",
                                  }}
                                  onClick={handleCheckboxClick}
                                >
                                  <input
                                    type="checkbox"
                                    checked={visibleColumns.postcode}
                                    onChange={() => toggleColumn("postcode")}
                                    onClick={handleCheckboxClick}
                                  />
                                  Postcode
                                </label>
                                <label
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    marginBottom: "8px",
                                    fontSize: "13px",
                                    cursor: "pointer",
                                  }}
                                  onClick={handleCheckboxClick}
                                >
                                  <input
                                    type="checkbox"
                                    checked={visibleColumns.actions}
                                    onChange={() => toggleColumn("actions")}
                                    onClick={handleCheckboxClick}
                                  />
                                  Actions
                                </label>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <table className="table table-hover dt-responsive users-table-wrapper">
                    <thead className="users-table">
                      <tr>
                        {visibleColumns.propertyName && <th>Property Name</th>}
                        {visibleColumns.propertyId && <th>Property ID</th>}
                        {visibleColumns.vacantRoomsNumber && (
                          <th>Vacant rooms number</th>
                        )}
                        {visibleColumns.vacantRoomsNames && (
                          <th>Vacant rooms names</th>
                        )}
                        {visibleColumns.postcode && <th>Postcode</th>}
                        {visibleColumns.actions && (
                          <th className="action-head">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedProperties.map((property, index) => (
                        <tr key={index}>
                          {visibleColumns.propertyName && (
                            <td>{property.propertyName || "-"}</td>
                          )}
                          {visibleColumns.propertyId && (
                            <td>{property.propertyId || "-"}</td>
                          )}
                          {visibleColumns.vacantRoomsNumber && (
                            <td>{property.vacantRoomsNumber ?? "-"}</td>
                          )}
                          {visibleColumns.vacantRoomsNames && (
                            <td>
                              {Array.isArray(property.vacantRoomsNames)
                                ? property.vacantRoomsNames.join(", ")
                                : "-"}
                            </td>
                          )}
                          {visibleColumns.postcode && (
                            <td>{property.postcode || "-"}</td>
                          )}
                          {visibleColumns.actions && (
                            <td>
                              <div
                                className="btn-group vacant-rooms-actions"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "550px",
                                }}
                                role="group"
                              >
                                <button
                                  type="button"
                                  className="btn btn-sm btn-warning"
                                  onClick={() => handleShow(property.id)}
                                  title="Show property details"
                                >
                                  Show
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={() => handleEdit(property.id)}
                                  title="Edit property"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger"
                                  onClick={() =>
                                    handleRemove(
                                      property.id,
                                      property.propertyName
                                    )
                                  }
                                  title="Remove property"
                                >
                                  Remove
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-info"
                                  onClick={() => handlePayments(property.id)}
                                  title="View payments"
                                >
                                  Payments
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-info"
                                  onClick={() => handlePhotos(property.id)}
                                  title="Manage photos"
                                >
                                  Photos
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-info"
                                  onClick={() => handleViewings(property.id)}
                                  title="View property viewings"
                                >
                                  Viewings
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-info"
                                  onClick={() =>
                                    handleMonthlyInspections(property.id)
                                  }
                                  title="Manage monthly inspections"
                                >
                                  Monthly Inspections
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-warning"
                                  onClick={() => handleCopyAddress(property)}
                                  title="Copy address to clipboard"
                                >
                                  <i className="glyphicon glyphicon-road"></i>{" "}
                                  Copy address
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination Controls */}
                  <div className="row mt-3">
                    <div className="col-md-6">
                      <p>
                        Showing {startIndex + 1} to{" "}
                        {Math.min(endIndex, filteredProperties.length)} of{" "}
                        {filteredProperties.length} entries
                      </p>
                    </div>
                    <div className="col-md-6">
                      <nav aria-label="Table pagination">
                        <ul className="pagination pagination-sm justify-content-end">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              Previous
                            </button>
                          </li>
                          {Array.from(
                            { length: Math.min(5, totalPages) },
                            (_, i) => {
                              const page = i + 1;
                              return (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            }
                          )}
                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VacantRooms;
